import { Children, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { FileTextIcon } from '@navikt/aksel-icons';
import classes from '../SurveysPage.module.css';

type ListPanelProps = {
  /** Kolonneoverskriftene for svar og resultat/status. */
  columns: [string, string];
  isLoading: boolean;
  error?: { message: string; onRetry: () => void };
  empty: { title: string; description: string };
  children: ReactNode;
};

/** Hvit flate med kolonneoverskrifter og radene, eller lasting/tom tilstand. */
export const ListPanel = ({ columns, isLoading, error, empty, children }: ListPanelProps) => {
  const { t } = useTranslation();
  const hasRows = Children.count(children) > 0;

  return (
    <div className={classes.panelStack}>
      {error && (
        <Alert data-color="warning">
          <Paragraph>{error.message}</Paragraph>
          <Button variant="secondary" data-size="sm" className={classes.alertAction} onClick={error.onRetry}>
            {t('surveys.page.error.retry')}
          </Button>
        </Alert>
      )}
      <section className={classes.panel} aria-busy={isLoading}>
        {isLoading ? (
          <LoadingRows label={t('surveys.page.loading')} />
        ) : hasRows ? (
          <>
            <div aria-hidden="true" className={classes.columnHeaders}>
              <span>{t('surveys.page.columns.survey')}</span>
              <span>{columns[0]}</span>
              <span>{columns[1]}</span>
            </div>
            <ul className={classes.rows}>{children}</ul>
          </>
        ) : (
          <EmptyPanel title={empty.title} description={empty.description} />
        )}
      </section>
    </div>
  );
};

export const EmptyPanel = ({
  title,
  description,
  icon = <FileTextIcon />,
  action,
}: {
  title: string;
  description: string;
  icon?: ReactNode;
  action?: ReactNode;
}) => (
  <div className={classes.empty}>
    <span aria-hidden="true" className={classes.emptyIcon}>
      {icon}
    </span>
    <Heading level={2} data-size="xs">
      {title}
    </Heading>
    <Paragraph data-size="sm" className={classes.emptyText}>
      {description}
    </Paragraph>
    {action}
  </div>
);

const LoadingRows = ({ label }: { label: string }) => (
  <div role="status" aria-label={label}>
    {[0, 1, 2].map((index) => (
      <div key={index} className={classes.loadingRow}>
        <Skeleton variant="rectangle" width={44} height={44} />
        <div className={classes.loadingText}>
          <Skeleton variant="text" width="25%" />
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="35%" />
        </div>
      </div>
    ))}
  </div>
);
